import { Component, h, Prop, Event, EventEmitter, State, Element } from "@stencil/core";

@Component({
  tag: "datagrid-filter-dropdown-logic",
  styleUrl: "signet-datagrid-filters.scss",
  shadow: false,
})
export class DatagridFilterDropdownLogic {
  @Element() el!: HTMLElement;
  /**
   * The ID of the grid to which this filter belongs.
   * This is used to identify which grid the filter is associated with.
   * It is passed to the filterChanged event when the filter value changes.
   * @example "grid-1"
   * @type {string}
   * @required
   * @description The ID of the grid to which this filter belongs.
   * This is used to identify which grid the filter is associated with.
   * It is passed to the filterChanged event when the filter value changes.
   * @default ""
   */
  @Prop() grid_id!: string;
  /**
   * The field name to which this filter is applied.
   * This is used to identify which field the filter is associated with.
   * It is passed to the filterChanged event when the filter value changes.
   * @example "eligible_year"
   * @type {string}
   * @required
   * @description The field name to which this filter is applied.
   * This is used to identify which field the filter is associated with.
   * It is passed to the filterChanged event when the filter value changes.
   * @default ""
   */
  @Prop() field!: string;
  /**
   * The label to be displayed for this filter.
   * This is used to identify the filter in the UI.
   * @example "Eligible Year"
   * @type {string}
   * @required
   * @description The label to be displayed for this filter.
   * This is used to identify the filter in the UI.
   * @default ""
   */
  @Prop() label!: string;
  /**
   * The options to be displayed in the filter.
   * This is a JSON string that contains an array of objects with label and value properties.
   * @example '[{"label": "2021", "value": "2021"}, {"label": "2022", "value": "2022"}]'
   */
  @Prop() options!: string;
  /**
   * The initial value of the filter.
   * This is used to set the initial state of the filter when it is first rendered.
   */
  @Prop() initialValue?: string;
  /**
   * The type of the filter.
   * This is used to identify the type of filter in the UI.
   */
  @Prop() type: string;

  
  @Event() filterChanged: EventEmitter<{
    grid_id: string;
    field: string;
    value: number;
    type: string;
    condition: string;
  }>;

  @State() selectedValue: string = this.initialValue || "";
  @State() selectedValues: string[] = [];
  @State() condition = "=";
  @State() selectedCondition = false;
  @State() value: any = "";

  handleConditionChange(event: CustomEvent) {
    // console.log("🔄 Condition Changed: ", event.detail);

    // Ensure condition is never null
    this.condition = event.detail || "=";

    this.selectedCondition = false;
    this.value = ""; // Reset value when condition changes
    this.emitFilterChange();
  }

  handleValueChange(event: Event) {
    // const target = event.target as HTMLInputElement;
    this.value = event;
    // console.log("✍️ Value Changed:", this.value);
    this.emitFilterChange();
  }

  emitFilterChange() {
    if (this.value === "" || this.value === null || this.value === undefined) {
      console.warn("⚠️ Skipping emit: No value set for filtering.");
      return;
    }

    const selectedValue = parseFloat(this.value);
    // console.log("🔍 Emitting filter change:", this.grid_id, this.field, selectedValue, this.condition);

    // Vue 2: emit 'input' event
    this.el.dispatchEvent(new CustomEvent('input', {
      bubbles: true,
      composed: true,
      detail: {
        grid_id: this.grid_id,
        field: this.field,
        value: selectedValue,
        type: this.type,
        condition: this.condition,
      },
    }));

    // Vue 3: emit 'update:modelValue' event
    this.el.dispatchEvent(new CustomEvent('update:modelValue', {
      bubbles: true,
      composed: true,
      detail: {
        grid_id: this.grid_id,
        field: this.field,
        value: selectedValue,
        type: this.type,
        condition: this.condition,
      },
    }));

    this.filterChanged.emit({
      grid_id: this.grid_id,
      field: this.field,
      value: selectedValue, // Ensure value is a number
      type: this.type,
      condition: this.condition,
    });
  }

  get parsedOptions(): { label: string; value: string }[] {
    try {
      const parsed = this.options ? JSON.parse(this.options) : [];
      if (Array.isArray(parsed)) {
        return parsed.map((opt) =>
          typeof opt === "string" ? { label: opt, value: opt } : opt
        );
      }
      return [];
    } catch (e) {
      console.error("🚨 Error parsing options:", this.options, e);
      return [];
    }
  }

  render() {
    return (
      <div class="datagrid-filter-dropdown-logic">
        <signet-select
          label="Condition"
          onSelectedValue={(e: CustomEvent) => {
            this.handleConditionChange(e);
          }}
        >
          <signet-option value=">=" label="At Least (≥)"></signet-option>
          <signet-option value="<=" label="At Most (≤)"></signet-option>
          <signet-option value="=" label="Exactly (=)"></signet-option>
        </signet-select>
        <signet-input
          disabled={this.selectedCondition}
          label={this.label}
          onInput={(e: any) => this.handleValueChange(e.target.value)}
        ></signet-input>
      </div>
    );
  }
}
